import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  fetchClassesForWeek,
  scheduleClassForWeek,
} from "../api/classesApi";

function CopyFromWeekDialog({
  open,
  onClose,
  campusName,
  currentWeek,
  allWeeks,
  formatWeekLabel,
  onCopied,
  showToast,
}) {
  const candidateWeeks = useMemo(() => {
    if (!currentWeek) return [];
    return allWeeks
      .filter((w) => w.id !== currentWeek.id)
      .slice()
      .sort(
        (a, b) => dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
      );
  }, [allWeeks, currentWeek]);

  const previousWeek = useMemo(() => {
    if (!currentWeek) return null;
    return (
      allWeeks
        .filter((w) => dayjs(w.startDate).isBefore(dayjs(currentWeek.startDate)))
        .sort(
          (a, b) =>
            dayjs(b.startDate).valueOf() - dayjs(a.startDate).valueOf()
        )[0] || candidateWeeks[0] || null
    );
  }, [allWeeks, currentWeek, candidateWeeks]);

  const [sourceWeekId, setSourceWeekId] = useState("");
  const [sourceClasses, setSourceClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    if (open) {
      setSourceWeekId(previousWeek?.id ?? "");
      setSourceClasses([]);
    }
  }, [open, previousWeek]);

  useEffect(() => {
    if (!open || !sourceWeekId) {
      setSourceClasses([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchClassesForWeek(sourceWeekId)
      .then((data) => {
        if (cancelled) return;
        setSourceClasses(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to fetch classes for source week", err);
        if (!cancelled) setSourceClasses([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, sourceWeekId]);

  const handleCopyOne = async (cls) => {
    if (!currentWeek?.id) return;
    setPendingId(cls.id);
    try {
      await scheduleClassForWeek(cls.id, currentWeek.id);
      showToast?.(`"${cls.groupName}" copied to this week`, "success");
      setSourceClasses((prev) => prev.filter((c) => c.id !== cls.id));
      onCopied?.();
    } catch (err) {
      console.error("Failed to copy class", err);
      showToast?.(err.message || "Failed to copy class", "error");
    } finally {
      setPendingId(null);
    }
  };

  const handleCopyAll = async () => {
    if (!currentWeek?.id || sourceClasses.length === 0) return;
    setCopying(true);
    let copied = 0;
    let failed = 0;
    for (const cls of sourceClasses) {
      try {
        await scheduleClassForWeek(cls.id, currentWeek.id);
        copied++;
      } catch (err) {
        console.error(`Failed to copy ${cls.groupName}`, err);
        failed++;
      }
    }
    if (copied > 0) onCopied?.();
    if (failed === 0) {
      showToast?.(`Copied ${copied} class(es) to this week`, "success");
    } else {
      showToast?.(
        `Copied ${copied}, failed ${failed}. See console for details.`,
        "warning"
      );
    }
    setSourceClasses([]);
    setCopying(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy classes to this week</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>Copy from week</InputLabel>
            <Select
              value={sourceWeekId}
              label="Copy from week"
              onChange={(e) => setSourceWeekId(e.target.value)}
            >
              {candidateWeeks.length === 0 && (
                <MenuItem value="" disabled>
                  No other weeks available
                </MenuItem>
              )}
              {candidateWeeks.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {formatWeekLabel ? formatWeekLabel(w) : `Week ${w.weekNumber}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loading ? (
            <Box py={3} display="flex" justifyContent="center">
              <CircularProgress size={28} />
            </Box>
          ) : sourceClasses.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
              No classes scheduled for the selected week.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary">
                Click a class to copy just that one, or use Copy all below.
              </Typography>
              <List dense>
                {sourceClasses.map((cls) => (
                  <ListItemButton
                    key={cls.id}
                    onClick={() => handleCopyOne(cls)}
                    disabled={copying || pendingId === cls.id}
                  >
                    <ListItemText
                      primary={cls.groupName}
                      secondary={cls.leaderName ? `Teacher: ${cls.leaderName}` : "No teacher assigned"}
                    />
                    {pendingId === cls.id && <CircularProgress size={18} />}
                  </ListItemButton>
                ))}
              </List>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleCopyAll}
          disabled={copying || loading || sourceClasses.length === 0}
        >
          {copying ? "Copying..." : `Copy all (${sourceClasses.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CopyFromWeekDialog;
